import { IconButton, TableCell } from "@material-ui/core";
import { Linked, useCollection, useIO, useLink, useLinked } from "@type-r/react";
import { asCurrency, asDate, CollapsableSection, Column, Columns, CsvDownloader, CsvMapping, Flex, FormCheckbox, InlineTable, Loading, PageTileField, PopupIODialog, PrimaryButton, TableHeader, TableRow } from 'lqx-react';
import * as React from 'react';
import { InvoiceMatch, Payment, Recommendation, RecsInvoice, execRecommendationSelection } from 'src/app/pages/inmatch/api';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Collection } from "@type-r/models";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";
import { Can } from "src/app/services/can";

export const InmatchRecommendations = ({ payment, disabled, expanded } : {
    payment : Payment, 
    disabled? : boolean,
    expanded? : boolean
}) => {

    const isReady = useIO( async () => {
        await payment.recommendations.fetch({params: {paymentId: payment.id}});
        payment.recommendations.each(recom => recom.invoices = payment.invoices);
    });

    
    const overlapping = React.useMemo(() => {
        if (payment.recommendations.length && payment.recommendations.length > 1) {
            return payment.recommendations.first().matches.filter(inv => !!payment.recommendations.last().matches.find(i => i.invoiceId === inv.invoiceId));
        } else {
            return [];
        }
    },
    [payment.recommendations['_changeToken']]);

    const $compareView = useLinked<boolean>(false);
    const $recommendation = useLink<Recommendation>(null);
    const selectedRecommendations = useCollection.subsetOf(payment.recommendations);

    React.useEffect(() => {
        if (selectedRecommendations.length === 3) {
            selectedRecommendations.shift();
        }
    }, [selectedRecommendations['_changeToken']]);

    return (
        <CollapsableSection notExpanded={!expanded}
            title="Inmatch Recommendations"
            right={
                payment.recommendations.length ?
                <PrimaryButton
                    color="yellow"
                    onClick={ () => $compareView.true() }
                >
                    Compare
                </PrimaryButton> : undefined
            }
        > { isReady ?
            <> 
                <InlineTable
                    head={
                        <TableHeader>
                            <TableCell />
                            <TableCell>
                                # of Invoices
                            </TableCell>
                            <TableCell>
                                Type
                            </TableCell>
                            <TableCell>
                                Date Range
                            </TableCell>
                            <TableCell>
                                Amount
                            </TableCell>
                            <TableCell>
                                Delta
                            </TableCell>
                            <TableCell/>
                        </TableHeader>
                    }
                >
                    { payment.recommendations.map(recommendation => (
                        <TableRow key={ recommendation.cid }>
                            <TableCell>
                                <FormCheckbox $checked={ selectedRecommendations.$includes(recommendation) } />
                            </TableCell>
                            <TableCell>
                                { recommendation.matches.length }
                            </TableCell>
                            <TableCell>
                                { recommendation.originTitle }
                            </TableCell>
                            <TableCell>
                                { recommendation.rangeTitle }
                            </TableCell>
                            <TableCell>
                                { asCurrency(recommendation.amount, payment.currency) }
                            </TableCell>
                            <TableCell>
                                { asCurrency(payment.unreconciledAmount - recommendation.amount, payment.currency) }
                            </TableCell>
                            <TableCell >
                                <RecommendationDownloader recom={recommendation} />
                            </TableCell>
                        </TableRow>
                    ))}                
                </InlineTable> 
                { $compareView.value ?
                    <RecommendationCompareDialog payment={payment} $recommendation={$recommendation} $compareView={$compareView} overlapping={overlapping} 
                        recommendations={selectedRecommendations} />
                : null }
                </>:
                <Loading/> }
        </CollapsableSection>
    )
}

const RecommendationCompareDialog = ({payment, $recommendation, $compareView, overlapping, recommendations } : {
    payment: Payment, 
    $recommendation: Linked<Recommendation>, 
    $compareView: Linked<boolean>,
    overlapping: InvoiceMatch[],
    recommendations : Collection<Recommendation> }, ) =>{
    
    return (
    <PopupIODialog
        $isOpen={$compareView}
        title="Select Recommendation"
        actionLabel="Confirm"
        maxWidth="xl"
        disabled={!$recommendation.value || !Can(UserEntitlementCatalogs.BankEntryWrite)}
        doIO={async () => {
            await execRecommendationSelection($recommendation.value.id, payment.id);
            await payment.matches.fetch();
        }}
            success="Recommendation selected"
        >
            <Columns>
                {recommendations.map(recom =>(
                    <Column>
                        <Flex column>
                            <Flex row wrap>
                                <PageTileField label="Invoices:">
                                 { recom.matches.length }
                                </PageTileField>
                                
                                <PageTileField label="Origin:">
                                    {recom.origin}
                                </PageTileField>
                                
                                <PageTileField label="Range:">
                                    {recom.rangeTitle}
                                </PageTileField>

                                <PageTileField label="Amount:">
                                    {asCurrency(recom.amount, payment.currency)}
                                </PageTileField>
                                
                                <PageTileField label="Delta:">
                                    {asCurrency(payment.unreconciledAmount - recom.amount)}
                                </PageTileField>

                                <PageTileField label="">
                                    <RecommendationDownloader recom={recom} />
                                </PageTileField>
                                <Flex grow />

                                <PrimaryButton 
                                    color={$recommendation.value && $recommendation.value.id === recom.id ? 'yellow' : 'lightGrey'}
                                    variant={$recommendation.value && $recommendation.value.id === recom.id ? 'contained' : 'outlined'}
                                    onClick={() => $recommendation.set(recom)}
                                >
                                    { recom.originTitle }
                                </PrimaryButton>
                            </Flex>
                        </Flex>
                        
                        <InlineTable
                            head={
                                <TableHeader>
                                    <TableCell>
                                        Invoice
                                    </TableCell>
                                    <TableCell>
                                        Due Date
                                    </TableCell>
                                    <TableCell>
                                        Amount
                                    </TableCell>
                                    <TableCell>
                                        Days to due
                                    </TableCell>
                                    <TableCell>
                                        Ref ID
                                    </TableCell>
                                </TableHeader>
                            }
                        >          {recom.matches ? <RecommendationMatchRow matches={recom.matches} overlapping={overlapping} /> : undefined}
                        </InlineTable>
                    </Column>
                ))}
            </Columns>
    </PopupIODialog>
    )
};

const RecommendationMatchRow = ({matches, overlapping} : {matches: Collection<InvoiceMatch>, overlapping: InvoiceMatch[]}) => {
    const sorted = React.useMemo(() => {
        if (matches) {
            const unique = matches.filter(inv => !overlapping.some(m => m.invoiceId === inv.invoiceId));
            return overlapping.concat(unique);
        }
    },
    [ ] );
    return (<>{ sorted.map(inv => (
        <TableRow key={ inv.cid } className={ overlapping.find(i => i.id === inv.id) ? 'green' : ''}>
            <TableCell>
                { inv.invoice.invoiceNumber }
            </TableCell>
            <TableCell>
                { asDate(inv.invoice.invoiceDueDate) }
            </TableCell>
            <TableCell>
                { asCurrency(inv.appliedAmount, inv.invoice.currency)}
            </TableCell>
            <TableCell>
                { inv.invoice.age }
            </TableCell>
            <TableCell>
                { inv.invoice.additionalReferenceIdentifier }
            </TableCell>
            
        </TableRow>))}</>);
};

const RecommendationDownloader = ({recom} : {recom: Recommendation}) => {

    return  <>{recom && recom.matches.length ? 
        <CsvDownloader filename={`invoices_${recom.originTitle}_${recom.rangeTitle}`} mapping={ csvFields } data={ recom.matches.filter(x => !!x) }>
            <IconButton aria-label="Download" size="medium" style={ { visibility: 'visible' } }>
                <GetAppIcon/>
            </IconButton>
        </CsvDownloader> :  undefined}</>
}

const csvFields : CsvMapping<InvoiceMatch> = {
    "Invoice Number" : x => x.invoice.invoiceNumber,
    "Invoice Due Date" : x => asDate(x.invoice.invoiceDueDate),
    "Applied Amount" : x => x.appliedAmount,
    "Ref Id": x => x.invoice.additionalReferenceIdentifier,
    "Days To Due": x => x.invoice.age
};
